import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Search, Loader2, Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useStrategySettingsStore } from '@/stores/strategy-settings-store'
import type { BankTheme, ThemeCategory } from '@/stores/strategy-settings-store'
import { useAuthStore } from '@/stores/auth-store'

const categoryLabels: Record<ThemeCategory, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk Mitigation',
  local_presence: 'Local Presence',
  other: 'Other',
}

export function WinThemeBankSettings() {
  const { bankThemes, isLoadingBank, isSavingBank, loadBankThemes, addBankTheme, updateBankTheme, removeBankTheme } = useStrategySettingsStore()
  const role = useAuthStore(s => s.role)
  const isAdmin = role === 'admin'

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTheme, setEditingTheme] = useState<BankTheme | null>(null)

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<ThemeCategory>('experience')

  useEffect(() => {
    void loadBankThemes()
  }, [loadBankThemes])

  const handleSearch = () => {
    const filters: { search?: string; category?: ThemeCategory } = {}
    if (search) filters.search = search
    if (categoryFilter !== 'all') filters.category = categoryFilter as ThemeCategory
    void loadBankThemes(filters)
  }

  const handleOpenAdd = () => {
    setEditingTheme(null)
    setTitle('')
    setDescription('')
    setCategory('experience')
    setDialogOpen(true)
  }

  const handleOpenEdit = (theme: BankTheme) => {
    setEditingTheme(theme)
    setTitle(theme.title)
    setDescription(theme.description ?? '')
    setCategory(theme.category)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!title.trim()) return
    if (editingTheme) {
      await updateBankTheme(editingTheme.id, {
        title: title.trim(),
        description: description.trim() || null,
        category,
      })
    } else {
      await addBankTheme({
        title: title.trim(),
        description: description.trim() || null,
        category,
      })
    }
    setDialogOpen(false)
  }

  const handleRemove = async (id: string) => {
    await removeBankTheme(id)
  }

  if (isLoadingBank) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Win Theme Bank</h3>
          <p className="text-meta mt-0.5">Reusable themes that can be pulled into any bid.</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={handleOpenAdd}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Theme
          </Button>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-muted/50 rounded-lg p-3 text-meta">
          Only organisation admins can manage the theme bank.
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search themes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="pl-9 h-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v); setTimeout(handleSearch, 0) }}>
          <SelectTrigger className="w-40 h-9">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.entries(categoryLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Theme list */}
      {bankThemes.length === 0 ? (
        <div className="py-16 text-center">
          <div className="flex justify-center mb-4">
            <Lightbulb className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-medium">No themes in the bank yet</h4>
          <p className="text-meta mt-1 max-w-sm mx-auto">
            Add reusable themes here. When you define win themes on a bid, you can save them to this bank for reuse.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-xl bg-card shadow-sm divide-y divide-border">
          {bankThemes.map(theme => (
            <div key={theme.id} className="p-4 flex items-start justify-between group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">{theme.title}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] bg-muted/30 text-muted-foreground">
                    {categoryLabels[theme.category] ?? theme.category}
                  </span>
                </div>
                {theme.description && (
                  <p className="text-meta line-clamp-2">{theme.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] text-muted-foreground">Used in {theme.timesUsed} bid{theme.timesUsed !== 1 ? 's' : ''}</span>
                  {theme.timesUsed > 0 && (
                    <>
                      <span className="text-[10px] text-muted-foreground">&middot;</span>
                      <span className="text-[10px] text-[hsl(var(--status-success))]">
                        Won {theme.timesWon} of {theme.timesUsed}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button type="button" onClick={() => handleOpenEdit(theme)} className="p-1.5 rounded hover:bg-muted">
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                  <button type="button" onClick={() => void handleRemove(theme.id)} className="p-1.5 rounded hover:bg-destructive/10">
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info — help text with nothing to act on, so no accent. It was the
          loudest thing on the page and the least important, on every visit. */}
      <div className="bg-muted/40 rounded-xl p-4 flex gap-3">
        <Lightbulb className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-xs font-medium">Building your theme bank</div>
          <p className="text-xs text-foreground mt-0.5 leading-relaxed">
            When you define win themes on a bid, you can save them here for reuse. Over time, BidContext will track which themes correlate with winning bids.
          </p>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTheme ? 'Edit Theme' : 'Add Theme to Bank'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1.5">Title</label>
              <Input
                placeholder="e.g., Proven Track Record in Civil Infrastructure"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Category</label>
              <Select value={category} onValueChange={v => setCategory(v as ThemeCategory)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Description <span className="font-normal text-muted-foreground">(optional)</span></label>
              <textarea
                className="w-full border border-border rounded-lg p-3 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={3}
                placeholder="Describe this theme's key differentiator..."
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleSave()} disabled={!title.trim() || isSavingBank}>
              {isSavingBank ? 'Saving...' : editingTheme ? 'Save' : 'Add Theme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
